import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useBlocker } from "react-router-dom";
import { Toast, useToast, Modal, Button } from "@hiya/ui";
import {
  getReport,
  publishReport,
  saveReportDraft,
  getLabPdfUrl,
  getPublishedPdfUrl,
} from "../../../api/reports";
import {
  PRODUCT_LABELS,
  PHASE_LABELS,
  EXERCISE_FREQUENCY_LABELS,
  ACTIVITY_TYPE_LABELS,
  SLEEP_HOURS_LABELS,
  STRESS_LEVEL_LABELS,
  DIET_TYPE_LABELS,
  HORMONAL_CONDITION_LABELS,
  CHRONIC_CONDITION_LABELS,
  THERAPY_TYPE_LABELS,
  HORMONE_LABELS,
  BLEEDING_PATTERN_LABELS,
  NON_CYCLE_HORMONE_CODES,
  type HormoneCard,
  type MatchedScenario,
  type LifestyleData,
  type HealthConditionsData,
  type TreatmentHistoryData,
} from "../../../types/report";

import { useFetchData } from "../../../hooks/useFetchData";
import { DetailLoadingState, DetailErrorState, DetailNotFoundState } from "../../../components/DetailPageStates";
import { InfoCard } from "../../../components/InfoCard";
import { ContentSection } from "../../../components/ContentSection";
import { ConfirmPublishModal } from "../../../components/ConfirmPublishModal";
import { PublishedTimestamp } from "../../../components/PublishedTimestamp";
import { DocumentIcon, HormoneIcon, ScenarioIcon, CycleIcon, EyeIcon, SymptomIcon, UserIcon } from "../../../components/Icons";
import { formatDateTime } from "../../../utils/formatDate";
import { ReportPreviewModal } from "./ReportPreviewModal";

const CYCLE_DAY_SHORT: Record<string, string> = {
  day_3: "Day 3",
  day_21: "Day 21",
  combined: "Day 3 + Day 21",
};

const GOAL_LABELS: Record<string, string> = {
  cycle_awareness: "Understand my menstrual cycle (cycle awareness & general hormone insights)",
  fertility_planning: "Fertility planning & reproductive hormone assessment",
  irregular_cycles_pcos: "Investigate irregular cycles or PCOS-related concerns",
  perimenopause_menopause: "Perimenopause / menopause hormone monitoring",
  general_wellbeing: "General hormonal wellbeing check",
};


const NOT_PROVIDED = "Not provided";
const NONE_DECLARED = "None declared";
const NO_ANSWER = "No";

function lifestyleText(
  value: string | null,
  labels: Record<string, string>,
  other?: string | null,
): string {
  if (!value) return NOT_PROVIDED;
  if (value === "other" && other) return `Other: ${other}`;
  return labels[value] ?? value.replaceAll("_", " ");
}

function conditionText(
  value: string | null,
  labels: Record<string, string>,
  other?: string | null,
): string {
  // Clinical distinction: null (question skipped) vs "none" (explicit "no conditions")
  if (value === null) return NOT_PROVIDED;
  if (value === "none") return NONE_DECLARED;
  if (value === "other" && other) return `Other: ${other}`;
  return labels[value] ?? value.replaceAll("_", " ");
}

function bleedingText(pattern: string, other: string | null): string {
  // Match mobile app phrasing exactly (HH-232 cases 2, 3): "other" with free
  // text renders only the free text; canonical patterns use the mobile labels.
  if (pattern === "other" && other) return other;
  return BLEEDING_PATTERN_LABELS[pattern] ?? pattern.replaceAll("_", " ");
}

function hasNonCycleHormone(cards: ReadonlyArray<HormoneCard>): boolean {
  return cards.some((c) => NON_CYCLE_HORMONE_CODES.has(c.hormone_code));
}

function getReportSectionText(
  sections: ReadonlyArray<Record<string, unknown>>,
  name: string,
): string | null {
  // HH-238 case 2: 'non_cycle_context' lives in report_sections, not in
  // cycle_context.patient_context (which is patient-facing CYCLE context).
  for (const s of sections) {
    if (s.section !== name) continue;
    const content = s.content;
    if (content && typeof content === "object" && "text" in content) {
      const t = (content as { text?: unknown }).text;
      if (typeof t === "string" && t.trim()) return t.trim();
    }
  }
  return null;
}

function Field({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div>
      <span className="text-sm text-text-tertiary">{label}</span>
      <p className="text-sm text-text-primary">{value}</p>
    </div>
  );
}

function LifestyleSection({ data }: Readonly<{ data: LifestyleData }>) {
  const fields: { label: string; value: string }[] = [
    { label: "Exercise frequency", value: lifestyleText(data.exercise_frequency, EXERCISE_FREQUENCY_LABELS) },
    { label: "Activity types", value: lifestyleText(data.activity_types, ACTIVITY_TYPE_LABELS, data.activity_other) },
    { label: "Sleep hours", value: lifestyleText(data.sleep_hours, SLEEP_HOURS_LABELS) },
    { label: "Stress level", value: lifestyleText(data.stress_level, STRESS_LEVEL_LABELS) },
    { label: "Diet type", value: lifestyleText(data.diet_type, DIET_TYPE_LABELS, data.diet_other) },
  ];
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
      {fields.map((f) => <Field key={f.label} label={f.label} value={f.value} />)}
    </div>
  );
}

function HealthConditionsSection({ data }: Readonly<{ data: HealthConditionsData }>) {
  const fields: { label: string; value: string }[] = [
    { label: "Hormonal / Reproductive", value: conditionText(data.hormonal_conditions, HORMONAL_CONDITION_LABELS, data.hormonal_other) },
    { label: "Chronic / General", value: conditionText(data.chronic_conditions, CHRONIC_CONDITION_LABELS, data.chronic_other) },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {fields.map((f) => <Field key={f.label} label={f.label} value={f.value} />)}
    </div>
  );
}

function yesNoText(
  flag: boolean | null,
  value: string | null,
  labels?: Record<string, string>,
  other?: string | null,
): string {
  // null = question skipped, false = declared "No", true = declared "Yes".
  // For "Yes" with missing sub-detail we render "Not provided" (the sub-field
  // was not captured), never "No" — that would misrepresent patient intent.
  if (flag === null) return NOT_PROVIDED;
  if (flag === false) return NO_ANSWER;
  if (!value || !value.trim()) return NOT_PROVIDED;
  if (labels) {
    if (value === "other" && other) return `Other: ${other}`;
    return labels[value] ?? value.replaceAll("_", " ");
  }
  return value;
}

function TreatmentHistorySection({ data }: Readonly<{ data: TreatmentHistoryData }>) {
  const fields: { label: string; value: string }[] = [
    { label: "Hormonal therapy", value: yesNoText(data.hormonal_therapy, data.therapy_type, THERAPY_TYPE_LABELS, data.therapy_other) },
    { label: "Supplement", value: yesNoText(data.taking_supplements, data.supplements_detail) },
    { label: "Specific treatment plan", value: yesNoText(data.treatment_plan, data.treatment_detail) },
  ];
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {fields.map((f) => <Field key={f.label} label={f.label} value={f.value} />)}
    </div>
  );
}

function getValueColorClass(status: string): string {
  if (status === "low") return "text-brand-brown/70";
  if (status === "high") return "text-brand-brown";
  return "text-text-primary";
}

function computeMarkerPos(value: number, refRange: string | undefined, status: string): number {
  // HH-238 case 1: status is authoritative — pick the zone first, then fine-tune
  // within it using value vs the bounds. Reserved gaps between zones (2 percent
  // on each side) keep the marker visually inside its zone even on the boundary.
  const LOW = { min: 2, max: 30 };
  const NORMAL = { min: 34, max: 66 };
  const HIGH = { min: 70, max: 98 };
  const parts = refRange ? refRange.split("-") : [];
  const lo = parts.length === 2 ? parseFloat(parts[0] ?? "") : NaN;
  const hi = parts.length === 2 ? parseFloat(parts[1] ?? "") : NaN;
  const validRange = Number.isFinite(lo) && Number.isFinite(hi) && hi > lo;

  if (status === "low") {
    if (!validRange || lo <= 0) return (LOW.min + LOW.max) / 2;
    const ratio = Math.max(0, Math.min(value / lo, 1));
    return LOW.min + ratio * (LOW.max - LOW.min);
  }
  if (status === "high") {
    if (!validRange) return (HIGH.min + HIGH.max) / 2;
    const ratio = Math.max(0, Math.min((value - hi) / (hi - lo), 1));
    return HIGH.min + ratio * (HIGH.max - HIGH.min);
  }
  // normal
  if (!validRange) return (NORMAL.min + NORMAL.max) / 2;
  const ratio = Math.max(0, Math.min((value - lo) / (hi - lo), 1));
  return NORMAL.min + ratio * (NORMAL.max - NORMAL.min);
}

function HormoneCardItem({ card }: { card: HormoneCard }) {
  const isOutOfRange = card.status === "low" || card.status === "high";
  const valueColor = getValueColorClass(card.status);

  return (
    <div className="mb-6">
      {/* Card: name, level, reference range, range bar */}
      <InfoCard>
        <h4 className="text-sm font-semibold text-text-primary mb-2">
          {HORMONE_LABELS[card.hormone_code] ?? card.hormone_code} ({card.unit})
        </h4>
        <div className="flex items-start justify-between mb-1">
          <div>
            <span className="text-xs text-text-tertiary">Level</span>
            <div className="flex items-center gap-2">
              <p className={`text-xl font-bold ${valueColor}`}>{card.value}</p>
              {isOutOfRange && (
                <span className={`text-xs ${valueColor} flex items-center gap-1`}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
                  Out of normal range
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <span className="text-xs text-text-tertiary">Reference range:</span>
            <p className="text-sm font-medium text-text-primary">
              {card.reference_range} {card.unit}
            </p>
          </div>
        </div>
        {/* 3-zone range bar with positioned marker */}
        <div className="mt-3 relative">
          <div className="relative h-6">
            <div
              className="absolute flex flex-col items-center"
              style={{ left: `${computeMarkerPos(card.value, card.reference_range, card.status)}%`, transform: "translateX(-50%)" }}
            >
              <span className="text-xs font-semibold text-text-primary whitespace-nowrap flex items-center gap-0.5">
                <svg width="8" height="8" viewBox="0 0 8 8" fill="currentColor"><path d="M4 6L1 2h6L4 6z"/></svg>
                Level: {card.value}
              </span>
            </div>
          </div>
          <div className="flex h-3 rounded-full overflow-hidden">
            <div className="flex-1 bg-brand-brown/70" />
            <div className="flex-1 bg-brand-desert" />
            <div className="flex-1 bg-brand-brown" />
          </div>
        </div>
      </InfoCard>
      {/* Text sections outside the card, full-width */}
      {card.general_explanation && (
        <div className="mt-3">
          <h5 className="text-sm font-semibold text-text-primary">Universal Hormone Explanation</h5>
          <p className="text-sm text-text-primary mt-1">{card.general_explanation}</p>
        </div>
      )}
      {card.patient_interpretation && (
        <div className="mt-3">
          <h5 className="text-sm font-semibold text-text-primary">Case-Specific Output</h5>
          <p className="text-sm text-text-primary mt-1">{card.patient_interpretation}</p>
        </div>
      )}
      {card.doctor_interpretation && (
        <div className="mt-3">
          <h5 className="text-sm font-semibold text-text-primary">Doctor Interpretation</h5>
          <p className="text-sm text-text-primary mt-1">{card.doctor_interpretation}</p>
        </div>
      )}
    </div>
  );
}

function ScenarioItem({ scenario }: { scenario: MatchedScenario }) {
  return (
    <InfoCard>
      <h4 className="text-sm font-semibold text-text-primary mb-2">{scenario.pattern_name}</h4>
      <p className="text-sm text-text-primary">{scenario.clinical_interpretation}</p>
      {scenario.clinical_relevance_doctor && (
        <div className="mt-3 pt-3 border-t border-stroke-primary">
          <span className="text-xs text-text-tertiary">Clinical Relevance</span>
          <p className="text-sm text-text-primary mt-1">{scenario.clinical_relevance_doctor}</p>
        </div>
      )}
    </InfoCard>
  );
}

export function ReportReviewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast } = useToast();

  const { data: report, loading, error } = useFetchData(
    () => (id ? getReport(id) : Promise.reject(new Error("No id"))),
    [id],
  );

  const [publishing, setPublishing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const [finalAssessment, setFinalAssessment] = useState("");
  const [planNextSteps, setPlanNextSteps] = useState("");
  const [cycleSummary, setCycleSummary] = useState("");
  const [nonCycleSummary, setNonCycleSummary] = useState("");
  const [attestation1, setAttestation1] = useState(false);
  const [attestation2, setAttestation2] = useState(false);

  // Sync form state once when report first loads + remember the initial
  // values so the Back button can warn on unsaved edits (HH-232 case 4).
  const initialized = useRef(false);
  const initialValues = useRef<{ fa: string; pns: string; cs: string; ncs: string } | null>(null);
  useEffect(() => {
    if (report && !initialized.current) {
      initialized.current = true;
      const fa = report.final_assessment ?? "";
      const pns = report.plan_next_steps ?? "";
      const cs = report.cycle_summary ?? "";
      const ncs = report.non_cycle_summary ?? "";
      setFinalAssessment(fa);
      setPlanNextSteps(pns);
      setCycleSummary(cs);
      setNonCycleSummary(ncs);
      initialValues.current = { fa, pns, cs, ncs };
    }
  }, [report]);

  const isPublished = report?.status === "published";
  const canEdit = report?.status === "in_review" || report?.status === "assigned" || report?.status === "drafted";
  const cycleSummaryOk = !report?.cycle_context || cycleSummary.trim();
  const canPublish = canEdit && attestation1 && attestation2 && finalAssessment.trim() && planNextSteps.trim() && cycleSummaryOk;
  const isDirty =
    canEdit &&
    initialValues.current !== null &&
    (finalAssessment !== initialValues.current.fa ||
      planNextSteps !== initialValues.current.pns ||
      cycleSummary !== initialValues.current.cs ||
      nonCycleSummary !== initialValues.current.ncs);

  // Hold publish in-flight: blocker should not fire during the publish flow
  // (we navigate to /dashboard/results on success).
  const publishingRef = useRef(false);
  useEffect(() => { publishingRef.current = publishing; }, [publishing]);

  // Single guard for both the in-app Back button and any browser/back-button
  // navigation. useBlocker requires the data router (createBrowserRouter /
  // createMemoryRouter) — see main.tsx.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty &&
      !publishingRef.current &&
      currentLocation.pathname !== nextLocation.pathname,
  );
  // Mirror the blocker into a ref so async handlers see the latest state
  // after re-renders (the blocker captured by closure is frozen).
  const blockerRef = useRef(blocker);
  useEffect(() => { blockerRef.current = blocker; }, [blocker]);

  function getDraftPayload() {
    return {
      final_assessment: finalAssessment || null,
      plan_next_steps: planNextSteps || null,
      cycle_summary: cycleSummary || null,
      non_cycle_summary: nonCycleSummary || null,
    };
  }

  async function handleSaveDraft() {
    if (!id || !canEdit) return;
    try {
      setSaving(true);
      await saveReportDraft(id, getDraftPayload());
      showToast(`CASE ${report?.case_id ?? id} Result has been saved as Draft`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to save draft", "error");
    } finally {
      setSaving(false);
    }
  }

  async function handlePublish() {
    if (!id || !canPublish) return;
    try {
      setPublishing(true);
      // HH-232 case 6: only persist the draft when the doctor actually edited
      // a field. The previous unconditional save bumped drafted_at to ~now on
      // every publish, making "Draft saved" and "Published" timestamps match.
      if (isDirty) {
        await saveReportDraft(id, getDraftPayload());
      }
      await publishReport(id, true);
      setShowPublishModal(false);
      showToast(`Report for Kit ${report?.kit_code ?? id} has been published to the patient`);
      navigate("/dashboard/results");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to publish report", "error");
      setShowPublishModal(false);
    } finally {
      setPublishing(false);
    }
  }

  function handleBack() {
    // useBlocker intercepts when isDirty; otherwise navigate straight back.
    navigate("/dashboard/results");
  }

  async function handleSaveAndLeave() {
    if (!id || !canEdit || blocker.state !== "blocked") return;
    try {
      setLeaving(true);
      await saveReportDraft(id, getDraftPayload());
      // Re-check via the ref after the async save: the user may have
      // dismissed the modal (X / backdrop → blocker.reset()) while the
      // request was in flight. The blocker captured at call time is stale.
      if (blockerRef.current.state === "blocked") blockerRef.current.proceed();
    } catch (err) {
      // Only surface the error toast if the modal is still up; if the user
      // dismissed it during the save, showing a toast about a draft they
      // already abandoned would be misleading.
      if (blockerRef.current.state === "blocked") {
        showToast(err instanceof Error ? err.message : "Failed to save draft", "error");
      }
    } finally {
      setLeaving(false);
    }
  }

  function handleLeaveAnyway() {
    if (blocker.state === "blocked") blocker.proceed();
  }

  function handleLeaveModalClose() {
    if (blocker.state === "blocked") blocker.reset();
  }

  async function handleDownloadLabPdf() {
    if (!id) return;
    try {
      const url = await getLabPdfUrl(id);
      window.open(url, "_blank");
    } catch {
      showToast("Lab report PDF not available", "error");
    }
  }

  async function handleViewPublishedPdf() {
    if (!id) return;
    try {
      const result = await getPublishedPdfUrl(id);
      if (result.kind === "url") {
        window.open(result.url, "_blank");
      } else if (result.kind === "generating") {
        showToast("PDF is still being generated. Try again shortly.");
      }
      // 'unavailable' is a no-op: the button should only render when
      // has_published_pdf is true, so reaching this branch implies the
      // S3 object was deleted (e.g. account deletion) between the
      // detail fetch and the click. Stay silent rather than alarming.
    } catch {
      showToast("Failed to open the published report", "error");
    }
  }

  if (loading) return <DetailLoadingState />;
  if (error) return <DetailErrorState message={error} />;
  if (!report) return <DetailNotFoundState message="Report not found" />;

  return (
    <div>
      <Toast message={toast.message} visible={toast.visible} variant={toast.variant} />

      <button
        type="button"
        onClick={handleBack}
        aria-label="Back to Results Review"
        className="mb-4 inline-flex items-center gap-1 text-sm text-text-primary"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="15 18 9 12 15 6" />
        </svg>
        Back
      </button>

      <div className="flex flex-col gap-10">
        {/* hiya-backend-e357: corrected-result MVP — non-destructive warning
            shown when phi.kits.correction_received_at IS NOT NULL. Corrected
            values themselves live on the FML side; ops fetches them manually. */}
        {report.correction_received && (
          <div
            role="status"
            aria-live="polite"
            data-testid="correction-banner"
            className="rounded-xl border border-stroke-secondary bg-brand-brown-6 px-4 py-3 text-sm text-brand-brown"
          >
            <strong className="font-medium">Heads up:</strong>{" "}
            The lab sent a corrected/amended result for this kit — the values
            shown may be outdated. Review the corrected result on the lab side
            before publishing.
          </div>
        )}

        {/* Client Info General */}
        <ContentSection icon={<UserIcon />} title="Client Info" action={report.has_lab_pdf ? (
          <button
            onClick={handleDownloadLabPdf}
            className="h-10 px-4 rounded-xl bg-btn-action text-text-btn text-sm font-medium flex items-center gap-2"
          >
            Download Lab Report
          </button>
        ) : undefined}>
          <InfoCard>
            <h5 className="text-sm font-medium text-text-primary mb-3">General</h5>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
              <div>
                <span className="text-sm text-text-tertiary">Case ID</span>
                <p className="text-sm text-text-primary">{report.case_id}</p>
              </div>
              {report.patient_age != null && (
                <div>
                  <span className="text-sm text-text-tertiary">Age</span>
                  <p className="text-sm text-text-primary">{report.patient_age} years</p>
                </div>
              )}
              <div>
                <span className="text-sm text-text-tertiary">Product</span>
                <p className="text-sm text-text-primary">
                  {PRODUCT_LABELS[report.product_type] ?? report.product_type}
                </p>
              </div>
              <div>
                <span className="text-sm text-text-tertiary">Test Date</span>
                <p className="text-sm text-text-primary">
                  {report.test_date ? formatDateTime(report.test_date) : "-"}
                </p>
              </div>
              <div>
                <span className="text-sm text-text-tertiary">Day of cycle</span>
                <p className="text-sm text-text-primary">
                  {CYCLE_DAY_SHORT[report.cycle_phase] ?? report.cycle_phase}
                </p>
              </div>
            </div>

            {/* Cycle Info from onboarding */}
            {report.onboarding && (
              <div className="mt-4 pt-4 border-t border-stroke-primary">
                <h5 className="text-sm font-medium text-text-primary mb-3">Cycle Info</h5>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {report.onboarding.cycle_length_days != null && (
                    <div>
                      <span className="text-sm text-text-tertiary">Length</span>
                      <p className="text-sm text-text-primary">{report.onboarding.cycle_length_days} days</p>
                    </div>
                  )}
                  {report.onboarding.cycle_description && (
                    <div>
                      <span className="text-sm text-text-tertiary">Description</span>
                      <p className="text-sm text-text-primary capitalize">
                        {report.onboarding.cycle_description.replaceAll("_", " ")}
                      </p>
                    </div>
                  )}
                  {report.onboarding.cramps && (
                    <div>
                      <span className="text-sm text-text-tertiary">Cramps</span>
                      <p className="text-sm text-text-primary capitalize">{report.onboarding.cramps}</p>
                    </div>
                  )}
                  {report.onboarding.bleeding_pattern && (
                    <div>
                      <span className="text-sm text-text-tertiary">Bleeding</span>
                      <p className="text-sm text-text-primary">
                        {bleedingText(report.onboarding.bleeding_pattern, report.onboarding.bleeding_other)}
                      </p>
                    </div>
                  )}
                </div>
                {report.onboarding.goals.length > 0 && (
                  <div className="mt-3">
                    <span className="text-sm text-text-tertiary">Goal</span>
                    <p className="text-sm text-text-primary mt-1">
                      {report.onboarding.goals.map((g) => GOAL_LABELS[g] ?? g.replaceAll("_", " ")).join(", ")}
                    </p>
                  </div>
                )}
              </div>
            )}

          </InfoCard>
        </ContentSection>

        {/* Symptom Summary */}
        {report.onboarding && report.onboarding.symptoms.length > 0 && (
          <ContentSection icon={<SymptomIcon />} title="Symptom Summary">
            <ul className="list-disc list-inside text-sm text-text-primary space-y-1">
              {report.onboarding.symptoms.map((s) => (
                <li key={s} className="capitalize">{s.replaceAll("_", " ")}</li>
              ))}
            </ul>
            {report.onboarding.has_other_symptoms && report.onboarding.other_symptoms && (
              <div className="mt-3">
                <span className="text-sm font-medium text-text-primary">Other Symptoms</span>
                <p className="text-sm text-text-primary mt-1">{report.onboarding.other_symptoms}</p>
              </div>
            )}
          </ContentSection>
        )}

        {/* Lifestyle (HH-282) */}
        {report.onboarding && (
          <ContentSection icon={<UserIcon />} title="Lifestyle">
            <LifestyleSection data={report.onboarding.lifestyle} />
          </ContentSection>
        )}

        {/* Health Conditions (HH-282) */}
        {report.onboarding && (
          <ContentSection icon={<SymptomIcon />} title="Health Conditions">
            <HealthConditionsSection data={report.onboarding.health_conditions} />
          </ContentSection>
        )}

        {/* Treatment History (HH-282 QA follow-up) */}
        {report.onboarding && (
          <ContentSection icon={<DocumentIcon />} title="Treatment History">
            <TreatmentHistorySection data={report.onboarding.treatment_history} />
          </ContentSection>
        )}

        {/* Context */}
        {(() => {
          const nonCycleText = getReportSectionText(report.report_sections, "non_cycle_context");
          const showNonCycle = nonCycleText !== null && hasNonCycleHormone(report.hormone_cards);
          const showCycle = !!report.cycle_context?.doctor_context;
          if (!showCycle && !showNonCycle) return null;
          return (
            <ContentSection icon={<CycleIcon />} title="Context">
              {showCycle && (
                <div className={showNonCycle ? "mb-4" : ""}>
                  <span className="text-sm font-medium text-text-primary">Cycle Context</span>
                  <p className="text-sm text-text-primary mt-1">{report.cycle_context!.doctor_context}</p>
                </div>
              )}
              {showNonCycle && (
                <div>
                  <span className="text-sm font-medium text-text-primary">Non-cycle Context</span>
                  <p className="text-sm text-text-primary mt-1">{nonCycleText}</p>
                </div>
              )}
            </ContentSection>
          );
        })()}

        {/* Hormone Cards */}
        {report.hormone_cards.length > 0 && (
          <ContentSection icon={<HormoneIcon />} title="Hormone Results">
            <div className="flex flex-col">
              {report.hormone_cards.map((card) => (
                <HormoneCardItem key={card.hormone_code} card={card} />
              ))}
            </div>
          </ContentSection>
        )}

        {/* Matched Scenarios */}
        {report.matched_scenarios.length > 0 && (
          <ContentSection icon={<ScenarioIcon />} title="Pattern Reference">
            <div className="flex flex-col gap-4">
              {report.matched_scenarios.map((scenario) => (
                <ScenarioItem key={scenario.scenario_id} scenario={scenario} />
              ))}
            </div>
          </ContentSection>
        )}

        {/* Doctor-Authored Fields */}
        {canEdit && (
          <ContentSection icon={<DocumentIcon />} title="Doctor Assessment">
            <div className="flex flex-col gap-4">
              {report.cycle_context && (
                <div>
                  <label htmlFor="cycle-summary" className="text-sm font-medium text-text-primary block mb-1">
                    Cycle Summary <span className="text-status-high-text">*</span>
                  </label>
                  <textarea
                    id="cycle-summary"
                    value={cycleSummary}
                    onChange={(e) => setCycleSummary(e.target.value)}
                    rows={3}
                    aria-required="true"
                    className="w-full px-3 py-2 rounded-xl border border-stroke-primary bg-bg-primary text-sm text-text-primary placeholder:text-text-quaternary resize-y"
                    placeholder="Summarize cycle context findings..."
                  />
                </div>
              )}
              <div>
                <label htmlFor="non-cycle-summary" className="text-sm font-medium text-text-primary block mb-1">
                  Non-Cycle Summary
                </label>
                <textarea
                  id="non-cycle-summary"
                  value={nonCycleSummary}
                  onChange={(e) => setNonCycleSummary(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 rounded-xl border border-stroke-primary bg-bg-primary text-sm text-text-primary placeholder:text-text-quaternary resize-y"
                  placeholder="Summarize non-cycle findings (TSH, AMH, etc.)..."
                />
              </div>
              <div>
                <label htmlFor="final-assessment" className="text-sm font-medium text-text-primary block mb-1">
                  Final Assessment Note <span className="text-status-high-text">*</span>
                </label>
                <textarea
                  id="final-assessment"
                  value={finalAssessment}
                  onChange={(e) => setFinalAssessment(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-xl border border-stroke-primary bg-bg-primary text-sm text-text-primary placeholder:text-text-quaternary resize-y"
                  placeholder="Enter your final assessment..."
                />
              </div>
              <div>
                <label htmlFor="plan-next-steps" className="text-sm font-medium text-text-primary block mb-1">
                  Plan / Next Steps <span className="text-status-high-text">*</span>
                </label>
                <textarea
                  id="plan-next-steps"
                  value={planNextSteps}
                  onChange={(e) => setPlanNextSteps(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 rounded-xl border border-stroke-primary bg-bg-primary text-sm text-text-primary placeholder:text-text-quaternary resize-y"
                  placeholder="Enter plan and next steps..."
                />
              </div>
            </div>
          </ContentSection>
        )}

        {/* Read-only assessment for published (HH-238 case 3: include cycle + non-cycle summary) */}
        {isPublished && report.final_assessment && (
          <ContentSection icon={<DocumentIcon />} title="Doctor Assessment">
            <InfoCard>
              {report.cycle_summary && (
                <div className="mb-4">
                  <span className="text-sm text-text-tertiary">Cycle Summary</span>
                  <p className="text-sm text-text-primary mt-1">{report.cycle_summary}</p>
                </div>
              )}
              {report.non_cycle_summary && (
                <div className="mb-4">
                  <span className="text-sm text-text-tertiary">Non-Cycle Summary</span>
                  <p className="text-sm text-text-primary mt-1">{report.non_cycle_summary}</p>
                </div>
              )}
              <div className="mb-4">
                <span className="text-sm text-text-tertiary">Final Assessment Note</span>
                <p className="text-sm text-text-primary mt-1">{report.final_assessment}</p>
              </div>
              {report.plan_next_steps && (
                <div>
                  <span className="text-sm text-text-tertiary">Plan / Next Steps</span>
                  <p className="text-sm text-text-primary mt-1">{report.plan_next_steps}</p>
                </div>
              )}
            </InfoCard>
          </ContentSection>
        )}

        {/* Doctor Signature */}
        {(report.doctor_name || report.signature_url) && (
          <div className="mt-4">
            {report.doctor_name && (
              <p className="text-sm text-text-primary font-medium">{report.doctor_name}</p>
            )}
            {report.signature_url && (
              <>
                <span className="text-sm text-text-tertiary">Doctor Signature</span>
                <img src={report.signature_url} alt="Doctor signature" className="h-[80px] w-[120px] object-contain mt-1" />
              </>
            )}
          </div>
        )}
      </div>

      {/* Attestation + Actions */}
      {canEdit && (
        <div className="mt-8">
          <div className="flex flex-col gap-3 mb-6">
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attestation1}
                onChange={(e) => setAttestation1(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stroke-primary"
              />
              <span className="text-sm text-text-primary">
                I confirm I have reviewed all flagged hormone values.
              </span>
            </label>
            <label className="flex items-start gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={attestation2}
                onChange={(e) => setAttestation2(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-stroke-primary"
              />
              <span className="text-sm text-text-primary">
                I confirm the information is correct and sign the doctor's document.
              </span>
            </label>
          </div>

          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => setShowPreview(true)}
              className="h-[51px] px-6 rounded-2xl bg-bg-tertiary text-text-primary text-base font-semibold flex items-center gap-2"
            >
              <EyeIcon />
              Preview
            </button>
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              className="h-[51px] px-6 rounded-2xl bg-bg-tertiary text-text-primary text-base font-semibold disabled:opacity-50"
            >
              {saving ? "Saving..." : "Save to Drafts"}
            </button>
            <button
              onClick={() => setShowPublishModal(true)}
              disabled={!canPublish}
              className="h-[51px] px-6 rounded-2xl bg-btn-action text-text-btn text-base font-semibold disabled:opacity-50"
            >
              Publish to Patient
            </button>
          </div>
        </div>
      )}

      {isPublished && (
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <PublishedTimestamp
            publishedAt={report.published_at}
            draftedAt={report.drafted_at}
          />
          {report.has_published_pdf && (
            <button
              onClick={handleViewPublishedPdf}
              className="h-10 px-4 rounded-xl bg-btn-action text-text-btn text-sm font-medium flex items-center gap-2"
            >
              View Published Report
            </button>
          )}
        </div>
      )}

      <ConfirmPublishModal
        open={showPublishModal}
        title="Publish Report"
        message="Are you sure you want to publish this report? Once published, the results will be visible to the patient and cannot be edited."
        publishing={publishing}
        onClose={() => setShowPublishModal(false)}
        onConfirm={handlePublish}
      />

      <ReportPreviewModal
        open={showPreview}
        onClose={() => setShowPreview(false)}
        kitCode={report.kit_code}
        productType={PRODUCT_LABELS[report.product_type] ?? report.product_type}
        cyclePhase={PHASE_LABELS[report.cycle_phase] ?? report.cycle_phase}
        testDate={report.test_date ? formatDateTime(report.test_date) : "-"}
        patientAge={report.patient_age}
        cycleContext={report.cycle_context}
        hormoneCards={report.hormone_cards}
        cycleSummary={cycleSummary}
        nonCycleSummary={nonCycleSummary}
        finalAssessment={finalAssessment}
        planNextSteps={planNextSteps}
        doctorName={report.doctor_name}
        signatureUrl={report.signature_url}
      />

      <Modal
        open={blocker.state === "blocked"}
        title="Close editing?"
        onClose={handleLeaveModalClose}
      >
        <p className="text-base text-text-secondary">
          Are you sure you want to leave without saving changes?
        </p>
        <div className="flex gap-3 mt-4">
          <Button variant="secondary" onClick={handleLeaveAnyway}>
            Leave Anyway
          </Button>
          <Button onClick={handleSaveAndLeave} disabled={leaving}>
            {leaving ? "Saving..." : "Save to drafts"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
